import * as React from 'react'
import { useState } from 'react'
import { useIntl, Link } from 'gatsby-plugin-react-intl'
import { GatsbyImage, getImage, ImageDataLike } from "gatsby-plugin-image"
import { useBreakpoint } from 'gatsby-plugin-breakpoints'
import { useTransition, animated } from 'react-spring'
import { InfoSection } from './InfoSection'
import { LandTag } from '../tag/LandTag'
import { ActivityTag } from '../tag/ActivityTag'
import nature from 'images/ico_hiker.svg';
import beach from 'images/ico_sun_white.svg';
import city from 'images/ico_city.svg';
import { useSelectedMonthContext, getActiveMonth, getMonthIndexFromName } from 'context/SelectedMonthContext'
import { monthSlugs, months as monthNames } from 'services/centralStore';
import firstCapital from 'services/firstCapitalCase'
import ensureMonthIndexInRange from 'services/ensureMonthIndexInRange'
import { WeatherData } from 'templates/fragments/weather-data'
import { useEffect } from 'react'

const icons = {
    nature,
    beach,
    city
}
type FrontPageCardProps = {
    imageDataLike: ImageDataLike
    partOfTheWorld: string
    tags?: string[]
    location: string
    slug: string,
    weatherData: WeatherData,
    lazyLoad?: boolean
}


export const FrontPageCard = ({
    imageDataLike: backgroundImageSrc,
    partOfTheWorld,
    tags,
    location,
    weatherData,
    slug,
    lazyLoad
}: FrontPageCardProps) => {
    const [isHovered, setIsHovered] = useState(false);
    const [titleLineCount, setTitleLineCount] = useState(1);
    const breakpoints = useBreakpoint()
    const isNotMobile = !breakpoints.sm
    const intl = useIntl()
    const titleRef = React.useRef(null);

    const [months, dispatch] = useSelectedMonthContext();
    let month = getActiveMonth(months).name;
    if (month == 'all') {
        month = monthNames[ensureMonthIndexInRange(new Date().getMonth() + 1)];
    }
    const monthIndex = getMonthIndexFromName(month);
    let indexes = [monthIndex - 1, monthIndex + 1];
    indexes = indexes.map(idx => ensureMonthIndexInRange(idx));

    const [lastMonthIndex, nextMonthIndex] = indexes;
    const [lastMonth, nextMonth] = [monthNames[lastMonthIndex], monthNames[nextMonthIndex]]
    const [lastMonthTemperature, thisMonthTemperature, nextMonthTemperature] =
        [
            weatherData.tmax[lastMonthIndex],
            weatherData.tmax[monthIndex],
            weatherData.tmax[nextMonthIndex]
        ]
    const [seaTemperature, numberOfRainyDays] = [weatherData.sst[monthIndex], weatherData.wet[monthIndex]];
    const transitions = useTransition(isHovered, {
        from: { opacity: 0 },
        enter: { opacity: 1 },
        leave: { opacity: 0 },
        config: { duration: 400 },
    })

    const image = getImage(backgroundImageSrc)

    useEffect(() => {
        if (titleRef?.current) {
            setTitleLineCount(getTitleLineCount(titleRef.current));
        }
    })

    return (
        <div
            className="card__frontpage__wrapper"
            onMouseEnter={() => isNotMobile && setIsHovered(true)}
            onMouseLeave={() => isNotMobile && setIsHovered(false)}
        >
            <Link to={slug}>
                <GatsbyImage loading={lazyLoad ? 'lazy' : 'eager'} alt={''} image={image} className={'card__frontpage__image'} />
                <div className="card__frontpage__tags">
                    <LandTag locationName={partOfTheWorld} />
                    {tags.map(a =>
                        <ActivityTag
                            key={a}
                            activityIconSrc={(icons as any)[a]}
                            nameOfActivity={intl.formatMessage({ id: `common.activities.${a}` })}
                        />
                    )}

                </div>
                <div
                    className={`card__frontpage__info ${isHovered && thisMonthTemperature != null && 'card__frontpage__info--hovered'
                        } ${titleLineCount>1 && 'card__frontpage__info--multilinetitle'}`}
                >
                    <div ref={titleRef} className="h4 card__frontpage__title">{location}</div>
                    {thisMonthTemperature != null && <div className="row">
                        <div className="col col--is--4">
                            <InfoSection
                                season={getSeason({weatherData,index:monthIndex})}
                                sectionTitle={intl.formatMessage({ id: `common.months.${month}` })}
                                amount={Math.floor(thisMonthTemperature)}
                                isTemperature
                            />
                        </div>
                        <div className="col col--is--4">
                            {seaTemperature && <InfoSection
                                sectionTitle={firstCapital(intl.formatMessage({ id: "common.sea" }))}
                                amount={Math.floor(seaTemperature)}
                                isTemperature
                                isSeaTemperature
                            />}
                        </div>
                        <div className="col col--is--4">
                            <InfoSection
                                sectionTitle={firstCapital(intl.formatMessage({ id: "common.rainyDays" }))}
                                amount={Math.floor(numberOfRainyDays)}
                                isNumberOfDays
                            />
                        </div>
                    </div>}
                    {isNotMobile && thisMonthTemperature != null &&
                        transitions(
                            (styles, hovered) =>
                                hovered && (
                                    <animated.div
                                        className="card__frontpage__additional_info--hovered"
                                        style={styles}
                                    >
                                        <div className="row">
                                            <div className="col col--is--4">
                                                <InfoSection
                                                    season={getSeason({ weatherData, index: lastMonthIndex })}

                                                    sectionTitle={
                                                        intl.formatMessage({ id: `common.months.${lastMonth}` })
                                                    }
                                                    amount={
                                                        Math.floor(lastMonthTemperature)
                                                    }
                                                    isTemperature
                                                />
                                            </div>
                                            <div className="col col--is--4">
                                                <InfoSection
                                                    season={getSeason({ weatherData, index: monthIndex })}

                                                    sectionTitle={
                                                        intl.formatMessage({ id: `common.months.${month}` })
                                                    }
                                                    amount={
                                                        Math.floor(thisMonthTemperature)
                                                    }
                                                    isTemperature
                                                />
                                            </div>
                                            <div className="col col--is--4">
                                                <InfoSection
                                                    season={getSeason({ weatherData, index: nextMonthIndex })}

                                                    sectionTitle={intl.formatMessage({ id: `common.months.${nextMonth}` })}
                                                    amount={
                                                        Math.floor(nextMonthTemperature)
                                                    }
                                                    isTemperature
                                                />
                                            </div>
                                        </div>
                                    </animated.div>
                                )
                        )}
                </div>
            </Link>
        </div>
    )
}

const getSeason = ({ weatherData, index }: {weatherData: WeatherData, index: number}) => {
    if (weatherData.rainseason[index] >= 2) return 'rainy';
    if (weatherData.wet[index] >= 12) return 'cloudy';
    return 'sun'
}

const getTitleLineCount = (el: HTMLElement) => {
    var divHeight = el.offsetHeight
    
    var lines = Math.round(divHeight / 28);
    return lines;
}


